import { mapUtil } from "@stormwater-software/common";
import { LoadingMessage } from "@stormwater-software/lib-ui";
import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import InspectionContext from "../../../contexts/InspectionContext";
import { ProjectContext } from "../../../contexts/ProjectContext";
import uploadApi from "../../../utils/api/upload";
import { getOfflineSiteMapUploads } from "../../../db/db";
import { SiteMaps } from "../../Projects/Details/SiteMaps";
import { withOfflineFallback } from "../../../utils/offlineFallback";

/**
 * Annotate findings on project site maps
 */
export const InspectionMap: React.FC = () => {
  const [maps, setMaps] = useState<{ upload: any }[] | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const inspectionContext = useContext(InspectionContext);
  const projectContext = useContext(ProjectContext) as any;

  const applyMaps = (siteMaps: { upload: any }[] | null) => {
    setMaps(
      siteMaps
        ? siteMaps.filter(siteMap =>
            mapUtil.isSupportedMimeType(siteMap.upload.mime_type),
          )
        : [],
    );
  };

  const loadMaps = () => {
    projectContext.loadDocs(projectContext.project.id);

    // Neither docTypes nor project.index is reachable offline, and
    // navigator.onLine cannot be trusted, so fall back to the records cached at
    // download time whenever the request fails.
    withOfflineFallback(
      () =>
        uploadApi
          .docTypes("Active Site Maps", projectContext.project.id)
          .then<{ upload: any }[] | null>(docType => {
            if (docType.dt) {
              return uploadApi.project.index(
                projectContext.project.id,
                docType.dt.map((ele: any) => ele.id), // ProjectDocTypeModel['id']
              );
            }

            return null;
          }),
      () => getOfflineSiteMapUploads(projectContext.project.id),
    )
      .then(applyMaps)
      // Without this the promise rejected silently and `maps` stayed null,
      // leaving the tab stuck on its loading message.
      .catch(err => {
        console.error("InspectionMap site maps", err);
        applyMaps(null);
      });
  };

  useEffect(() => {
    /** @todo optimize these api calls. Use react-query */
    loadMaps();
  }, []);

  useEffect(() => {
    if (projectContext.projectDocs && Array.isArray(maps)) {
      setLoading(false);
    }
  }, [projectContext.projectDocs, maps]);

  if (loading) {
    return <LoadingMessage what="active site maps" showLoadingText />;
  }

  if (maps && maps.length === 0) {
    return (
      <p>
        No site maps have been uploaded. Please upload a site{" "}
        <Link to={`/projects/${projectContext.project.id}/project-docs`}>
          here
        </Link>
      </p>
    );
  }

  const handleDocumentUpload = () => {
    loadMaps();
  };

  const handleDocumentDelete = () => {
    // Reload maps after deletion
    loadMaps();
  };

  return (
    <section className="inspection-map">
      <SiteMaps
        siteMaps={(maps || []).map(map => map.upload)}
        id={projectContext.project.id}
        projectName={projectContext.project.projectName}
        showActions={true}
        inspectionId={inspectionContext.inspection.id}
        inspectionTemplateTypeId={inspectionContext.inspection?.inspection_template?.inspection_type_id}
        onDocumentUpload={handleDocumentUpload}
        onDocumentDelete={handleDocumentDelete}
      />
    </section>
  );
};

export default InspectionMap;
